import clsx from "clsx";
import Link from "next/link";
import { Fragment, type ReactNode } from "react";

type Props = {
  /** The accepted answers the panel greets by; either may be empty. */
  firstName: string;
  organisationName: string;
};

const PRETITLE = "Registration received";
const TITLE = "Thank you{first_name}.";
const TEXT = `We've received your DAS Centre registration for {organisation_name}.
A member of the DAS team will review the information you have provided and contact you to arrange an introductory discussion.
During that conversation, we'll learn more about your college, your learners and your plans for a DAS Academy, and explain the next steps towards Centre approval.`;
const STEPS_TITLE = "What happens next";

const STEPS = [
  {
    number: "01",
    title: "Initial discussion",
    text: "We learn more about your organisation, learners and ambitions.",
  },
  {
    number: "02",
    title: "Centre review",
    text: "Together we review the requirements for delivering DAS.",
  },
  {
    number: "03",
    title: "Centre approval",
    text: "Your organisation completes the DAS Centre approval process.",
  },
  {
    number: "04",
    title: "DAS Academy onboarding",
    text: "Your team is supported to prepare for delivery.",
  },
];

/** The buttons under the confirmation; the first is the filled one. */
const LINK_STYLES = {
  primary:
    "border-transparent bg-brand-blue font-semibold text-white hover:text-white hover:opacity-82 focus:text-white focus:opacity-82",
  secondary:
    "border-brand-blue/13 font-medium text-brand-blue hover:border-brand-blue/27 hover:text-brand-blue focus:border-brand-blue/27 focus:text-brand-blue",
} as const;

const LINKS: { label: string; url: string; style: keyof typeof LINK_STYLES }[] =
  [
    { label: "Return to DAS", url: "/", style: "primary" },
    {
      label: "Learn about DAS Academies",
      url: "/about-us",
      style: "secondary",
    },
    { label: "Contact the DAS team", url: "/contact", style: "secondary" },
  ];

/**
 * The port of fragments/forms/das-register-form/complete.php: the form's
 * success state, what takes the place of the form's body once a registration
 * has been accepted. On WordPress the handler rendered it and sent it back with
 * the response; here the form renders it from the answers it just sent, which
 * is what lets it greet the applicant by name.
 *
 * The two personalised phrases are holes rather than sentences: {first_name}
 * and {organisation_name} are filled in from the accepted submission. Both read
 * sensibly when they cannot be filled - the name reduces to nothing, so "Thank
 * you." still scans, and the organisation falls back to "your organisation".
 */
export default function RegistrationComplete({
  firstName,
  organisationName,
}: Props) {
  // The comma belongs to the name rather than to the sentence, so no name still reads "Thank you."
  const name = firstName ? `, ${firstName}` : "";
  const organisation = organisationName || "your organisation";

  const renderTokens = (value: string): ReactNode =>
    value.split(/(\{first_name\}|\{organisation_name\})/).map((part, index) => {
      if (part === "{first_name}") {
        return <Fragment key={index}>{name}</Fragment>;
      }

      if (part === "{organisation_name}") {
        return (
          <strong key={index} className="font-semibold text-brand-blue">
            {organisation}
          </strong>
        );
      }

      return <Fragment key={index}>{part}</Fragment>;
    });

  const lines = TEXT.split(/\r\n|\r|\n/).filter((line) => line.trim() !== "");

  return (
    <div className="outline-none" role="status" tabIndex={-1}>
      <span
        className="mb-7 flex size-14 items-center justify-center rounded-full border-[1.5px] border-das-green/16 bg-das-green/7 text-das-green"
        aria-hidden="true"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          focusable="false"
        >
          <path
            d="M4 12l5 5 11-11"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>

      <p className="m-0 mb-2.5 text-[11px] leading-[1.4] font-medium tracking-[0.22em] text-das-green uppercase">
        {PRETITLE}
      </p>

      {/* No bottom margin: the theme's h3[class] reset outranked this heading's own margin, and staging shows none. */}
      <h3 className="m-0 text-[length:clamp(32px,5vw,52px)] leading-[1.05] font-bold tracking-[-0.03em] text-brand-blue">
        {renderTokens(TITLE)}
      </h3>

      <div className="m-0 mb-12 max-w-[560px] mobile:mb-9">
        {lines.map((line, index) => (
          <p
            key={index}
            className="m-0 mb-2 text-[15px] leading-[1.82] text-[#5a6a7a] last:mb-0"
          >
            {renderTokens(line)}
          </p>
        ))}
      </div>

      <div className="mb-12 mobile:mb-9">
        <p className="m-0 mb-7 text-[13px] leading-[1.4] font-semibold tracking-[0.12em] text-brand-blue uppercase">
          {STEPS_TITLE}
        </p>

        <ol className="m-0 flex list-none flex-wrap gap-6 p-0">
          {STEPS.map((step) => (
            <li
              key={step.number}
              className="min-w-0 shrink-0 grow-0 basis-[calc(25%-18px)] max-w-[calc(25%-18px)] px-0 py-5 small-desktop:basis-[calc(50%-12px)] small-desktop:max-w-[calc(50%-12px)] mobile:basis-full mobile:max-w-full"
            >
              <span className="mb-3 flex size-7 items-center justify-center rounded-full border-[1.5px] border-das-green/27 bg-das-green/4 text-[10px] leading-none font-bold tracking-[0.06em] text-das-green">
                {step.number}
              </span>

              <p className="m-0 mb-1.5 text-[13.5px] leading-[1.4] font-bold tracking-[-0.01em] text-brand-blue">
                {step.title}
              </p>

              <p className="m-0 text-[13px] leading-[1.6] text-[#7a8a9a]">
                {step.text}
              </p>
            </li>
          ))}
        </ol>
      </div>

      <div className="flex flex-wrap gap-2.5">
        {LINKS.map((link) => (
          <Link
            key={link.url}
            className={clsx(
              "inline-flex items-center rounded-[100px] border-[1.5px] px-6 py-3 text-[13.5px] leading-[1.2]",
              "transition-[opacity,border-color] duration-400 ease-[ease]",
              LINK_STYLES[link.style],
            )}
            href={link.url}
          >
            {link.label}
          </Link>
        ))}
      </div>
    </div>
  );
}
